import type { SetURLSearchParams } from 'react-router';
import ButtonComponent from '../../../components/UI/Button/Button.component';

const CATEGORIES = ['SAFETY', 'INTEGRITY', 'LEGAL', 'QUALITY'] as const;

interface Props {
  isAdmin: boolean;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

function toggleMulti(
  params: URLSearchParams,
  setParams: SetURLSearchParams,
  key: string,
  value: string,
) {
  const current = (params.get(key) ?? '').split(',').filter(Boolean);
  const next = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
  const newParams = new URLSearchParams(params);
  if (next.length === 0) newParams.delete(key);
  else newParams.set(key, next.join(','));
  // Filter change invalidates any cursor — the new scan is a different result
  // set, resuming from an old cursor would skip legitimate first-page matches.
  newParams.delete('cursor');
  setParams(newParams);
}

export default function ReportFilters({
  isAdmin,
  searchParams,
  setSearchParams,
}: Props) {
  const activeCategories = (searchParams.get('category') ?? '')
    .split(',')
    .filter(Boolean);

  const anyActive =
    searchParams.has('category') ||
    searchParams.has('reason') ||
    searchParams.has('assignee');

  const clearAll = () => {
    const kept = new URLSearchParams(searchParams);
    kept.delete('category');
    kept.delete('reason');
    kept.delete('assignee');
    // Clearing filters resets the scan — any in-flight cursor is stale.
    kept.delete('cursor');
    setSearchParams(kept);
  };

  return (
    <div className="Moderation__Filters">
      <div
        className="Moderation__Filters__Group"
        role="group"
        aria-label="Category"
      >
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={activeCategories.includes(c)}
            onClick={() =>
              toggleMulti(searchParams, setSearchParams, 'category', c)
            }
            className={`Moderation__Chip Moderation__Chip--${c}${
              activeCategories.includes(c) ? ' is-active' : ''
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {isAdmin && (
        <div className="Moderation__Filters__Group">
          <label htmlFor="assignee-filter">Assignee</label>
          <input
            id="assignee-filter"
            type="text"
            value={searchParams.get('assignee') ?? ''}
            onChange={(e) => {
              const newParams = new URLSearchParams(searchParams);
              if (e.target.value) newParams.set('assignee', e.target.value);
              else newParams.delete('assignee');
              // Assignee change → new scan → stale cursor.
              newParams.delete('cursor');
              setSearchParams(newParams);
            }}
            placeholder="Moderator userId"
          />
        </div>
      )}

      {anyActive && (
        <ButtonComponent
          variant="ghost"
          size="sm"
          className="Moderation__Filters__Clear"
          onClick={clearAll}
        >
          Clear filters
        </ButtonComponent>
      )}
    </div>
  );
}
